import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { LOGO_SRC } from "./RevoraCard";

const LINKS = [
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Products", href: "/#products" },
  { label: "Placement", href: "/#placement" },
  { label: "FAQ", href: "/#faq" },
  { label: "Track Order", href: "/track" },
  { label: "Portal", href: "/portal" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      navigate("/");
      setTimeout(() => document.querySelector(href.slice(1))?.scrollIntoView({ behavior: "smooth" }), 80);
    } else {
      navigate(href);
    }
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/5" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={LOGO_SRC} alt="Revora Reviews" className="h-9 w-auto" />
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => go(l.href)}
              className="text-sm font-medium text-black/60 hover:text-ink transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta"
            onClick={() => go("/#products")}
            className="hidden sm:inline-flex items-center gap-2 bg-ink text-white text-sm font-bold px-6 py-2.5 rounded-full hover:bg-volt transition-colors duration-300 group"
          >
            Shop Products
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-ink"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white border-b border-black/5"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.label}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-2xl font-bold text-ink"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-cta"
                onClick={() => go("/#products")}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-volt text-ink font-bold px-6 py-3 rounded-full hover:bg-voltdark hover:text-white transition-colors duration-300"
              >
                Shop Products <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
